import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemButton,
    IconButton,
    Chip,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    Divider
} from '@mui/material';
import {
    Folder as FolderIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    Image as ImageIcon,
    VideoFile as VideoIcon,
    AudioFile as AudioIcon,
    PictureAsPdf as PdfIcon,
    TextFields as TextIcon,
    InsertDriveFile as FileIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

const getCategoryIcon = (category) => {
    switch (category) {
        case 'image': return <ImageIcon />;
        case 'video': return <VideoIcon />;
        case 'audio': return <AudioIcon />;
        case 'pdf': return <PdfIcon />;
        case 'text': return <TextIcon />;
        default: return <FileIcon />;
    }
};

const StimuliSetManager = ({ open, onClose, onStimuliSetSelected, experimentId = null }) => {
    const [stimuliSets, setStimuliSets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSet, setSelectedSet] = useState(null);
    const [error, setError] = useState('');
    
    const { getAccessTokenSilently } = useAuth0();

    const fetchStimuliSets = useCallback(async () => {
        setLoading(true);
        setError('');
        
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.get(
                `${API_URL}/api/stimuli/sets${experimentId ? `?experimentId=${experimentId}` : ''}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            setStimuliSets(response.data);
        } catch (err) {
            console.error('Error fetching stimuli sets:', err);
            setError('Failed to load stimuli sets');
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently, experimentId]);

    useEffect(() => {
        if (open) {
            fetchStimuliSets();
        }
    }, [open, fetchStimuliSets]);

    const deleteSet = async (setId) => {
        if (!window.confirm('Are you sure you want to delete this stimuli set?')) {
            return;
        }
        
        try {
            const token = await getAccessTokenSilently();
            await axios.delete(
                `${API_URL}/api/stimuli/sets/${setId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            // Refresh the list
            fetchStimuliSets();
        } catch (err) {
            console.error('Error deleting stimuli set:', err);
            setError('Failed to delete stimuli set');
        }
    };

    const selectSet = (stimuliSet) => {
        setSelectedSet(stimuliSet);
    };

    const confirmSelection = () => {
        if (selectedSet) {
            onStimuliSetSelected(selectedSet);
            onClose();
        }
    };

    const filteredSets = stimuliSets.filter(set =>
        set.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        set.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Select Stimuli Set</Typography>
                    <TextField
                        placeholder="Search sets..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 250 }}
                    />
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {selectedSet && (
                            <Card variant="outlined" sx={{ mb: 3, bgcolor: 'action.selected' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        Selected: {selectedSet.name}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        {selectedSet.description}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="body2">
                                            {selectedSet.files?.length || 0} files
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Created: {new Date(selectedSet.createdAt).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                    {selectedSet.files && selectedSet.files.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2" sx={{ mb: 1 }}>Files:</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {selectedSet.files.slice(0, 5).map((file, index) => (
                                                    <Chip
                                                        key={index}
                                                        icon={getCategoryIcon(file.category)}
                                                        label={file.title || file.originalName}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                                {selectedSet.files.length > 5 && (
                                                    <Chip
                                                        label={`+${selectedSet.files.length - 5} more`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        {filteredSets.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <FolderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="textSecondary">
                                    No stimuli sets found
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {searchTerm ? 'Try adjusting your search' : 'Create your first stimuli set to get started'}
                                </Typography>
                            </Box>
                        ) : (
                            <List>
                                {filteredSets.map((set) => (
                                    <ListItem
                                        key={set._id}
                                        disablePadding
                                        sx={{
                                            border: '1px solid',
                                            borderColor: selectedSet?._id === set._id ? 'primary.main' : 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                            bgcolor: selectedSet?._id === set._id ? 'action.selected' : 'background.paper'
                                        }}
                                    >
                                        <ListItemButton onClick={() => selectSet(set)}>
                                            <ListItemIcon>
                                                <FolderIcon />
                                            </ListItemIcon>
                                            
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Typography variant="subtitle1">
                                                            {set.name}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    // TODO: Implement edit functionality
                                                                }}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    deleteSet(set._id);
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                            {set.description || 'No description'}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {set.files?.length || 0} files
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Created: {new Date(set.createdAt).toLocaleDateString()}
                                                            </Typography>
                                                        </Box>
                                                        
                                                        {/* Show file categories */}
                                                        {set.files && set.files.length > 0 && (
                                                            <Box sx={{ mt: 1 }}>
                                                                {Array.from(new Set(set.files.map(f => f.category))).map(category => (
                                                                    <Chip
                                                                        key={category}
                                                                        icon={getCategoryIcon(category)}
                                                                        label={category}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ mr: 0.5 }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button 
                    onClick={confirmSelection} 
                    variant="contained"
                    disabled={!selectedSet}
                >
                    Select This Set
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StimuliSetManager;
